/* eslint-disable no-console */
import React, { ReactNode, useEffect, useState } from 'react';
import {
  gql, useLazyQuery, useMutation,
} from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  AuthorizationNotifier, AuthorizationServiceConfiguration, BaseTokenRequestHandler, FetchRequestor, RedirectRequestHandler,
} from '@openid/appauth';
import { jwtDecode } from 'jwt-decode';
import { datadogLogs } from '@datadog/browser-logs';
import auth0, { auth0RefreshToken, getOrganizationKeyFromURL, JWT_CLAIMS_NAMESPACE } from './auth0Client';
import { parseJwt } from '../util';
import { Box } from '../ovComponents/1-primative/box/box';
import { Typography } from '../ovComponents/1-primative/typography/typography';
import { CircularProgress } from '../ovComponents/2-component/circularProgress/circularProgress';
import { Dialog, DialogContent, DialogTitle } from '../ovComponents/2-component/dialog/dialog';
import { Button } from '../ovComponents/2-component/button/button';
import { PublicOrganizationSelectDialog } from '../ovComponents/3-pattern/publicOrganizationSelectDialog/publicOrganizationSelectDialog';
import { useAuthContext } from './ovApolloProvider';
import { Organization } from '../interfaces/organization';
import { useAuth } from './AuthProvider';
import { buildNewTokenRequest } from './CIAMProvider';
import { modifyFavIcon, modifyTabTitle, setOrgPublicSettings } from '../util/browserTabUpdates';

export const BASIC_ME = gql`
  query basicMe {
    me { id firstName lastName email permissions }
  }
`;

export const FETCH_ORGANIZATION_PUBLIC_SETTINGS = gql`
  query fetchOrganizationPublicSettings($input: FetchOrganizationPublicSettingsInput!) {
    fetchOrganizationPublicSettings(input: $input) {
      subdomain
      faviconLink
      browserTabTitle
      clientHostedLoginClientId
      clientHostedLoginURL
      clientHostedLoginScope
      authenticationType
      enterpriseConnectionName
      useHostedLogin
    }
  }
`;

export const EXCHANGE_CLIENT_TOKEN = gql`
  mutation exchangeClientToken($input: ExchangeClientTokenInput!) {
    exchangeClientToken(input: $input) { accessToken }
  }
`;

const authorizationHandler = new RedirectRequestHandler();
const notifier = new AuthorizationNotifier();
authorizationHandler.setAuthorizationNotifier(notifier);
const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());

const Auth0Provider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { loginPing } = useAuth();
  const { t } = useTranslation('authentication');
  const {
    setAuthToken, setIsEmbedded, setPermissions, appLogout,
  } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [openOrganizationSelectDialog, setOpenOrganizationSelectDialog] = useState(false);
  const [accessibleOrgs, setAccessibleOrgs] = useState<string[]>();
  const [openSignUpNotAllowedDialog, setSignUpNotAllowedDialog] = useState(false);
  const localStorageFavIcon = window.localStorage.getItem('faviconLink') || '';
  const localStorageBrowserTitle = window.localStorage.getItem('browserTabTitle') || '';
  const [faviconLink, setFaviconLink] = useState(localStorageFavIcon);
  const [browserTabTitle, setBrowserTabTitle] = useState(localStorageBrowserTitle);

  const clientHostedLoginToken = window.localStorage.getItem('clientHostedLoginToken') || undefined;
  const multipleOrgChosen = window.localStorage.getItem('multipleOrgChosen') === 'true';
  const isOneWealthDomain = window.location.host.includes('onevest.') || window.location.host.includes('onewealth.');

  const queryParams = new URLSearchParams(window.location.search);
  const invitiation = queryParams.get('invitation');
  const organization = queryParams.get('organization');
  const organizationName = queryParams.get('organization_name') || undefined;
  const state = queryParams.get('state');
  const connection = queryParams.get('connection');
  const allowPopup = queryParams.get('allow_popup');
  const externalAccessTokenParams = queryParams.get('accessToken');
  const isUseHostedLogin = queryParams.get('useHostedLogin');
  const authCode = queryParams.get('authCode');

  /**
   * If it's a isOneWealthDomain, we can safely assume that the organizationKey (aka subdomain) will be present in the first part of the host
   * e.g.: admin.staging.onevest.com, where `admin` would be the `organizationKey`
   *
   * In cases where it's not a isOneWealthDomain, meaning it's a custom domain/url, we need to fetch the organizationKey asynchronously
   * e.g.: wealth.partner.com
   */
  const { auth0RedirectUri, organizationKey } = isOneWealthDomain ? getOrganizationKeyFromURL(organizationName) : { auth0RedirectUri: window.location.origin, organizationKey: null };

  if (invitiation && organization) {
    // eslint-disable-next-line max-len
    window.location.href = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/authorize?invitation=${invitiation}&organization=${organization}&response_type=token&client_id=${process.env.REACT_APP_AUTH0_CLIENT_ID}&redirect_uri=${auth0RedirectUri}`;
  }

  const [basicMe] = useLazyQuery(BASIC_ME);
  const [fetchOrganizationPublicSettings] = useLazyQuery(FETCH_ORGANIZATION_PUBLIC_SETTINGS);
  const [exchangeClientTokenMutation] = useMutation(EXCHANGE_CLIENT_TOKEN);

  const fetchPermissions = async (token: string) => {
    const basicMeResp = await basicMe({
      context: { headers: { authorization: `Bearer ${token}` } },
      variables: { skipErrorHandler: true },
      onError: () => {
        setLoading(false);
        setSignUpNotAllowedDialog(true);
      },
    });
    const orgUser = basicMeResp?.data?.me;
    if (orgUser && orgUser.permissions) {
      setPermissions(orgUser.permissions);
      return true;
    }
    return false;
  };

  const handleCallback = async (issuerURL: string, redirectUrl: string, clientId: string, orgSubdomain: string) => {
    setupAuthenticationListener(issuerURL, redirectUrl, clientId, orgSubdomain);

    await authorizationHandler.completeAuthorizationRequestIfPossible();
  };

  const setupAuthenticationListener = (issuerURL: string, redirectUrl: string, clientId: string, orgSubdomain: string) => {
    notifier.setAuthorizationListener(async (authorizationRequest, authorizationResponse, authorizationError) => {
      if (authorizationError) {
        console.error(authorizationError);
      } else {
        const tokenRequest = await buildNewTokenRequest(clientId, redirectUrl, authorizationResponse?.code || '', authorizationRequest?.internal?.code_verifier || '');

        try {
          const config = await AuthorizationServiceConfiguration.fetchFromIssuer(
            issuerURL,
            new FetchRequestor(),
          );
          const { accessToken, refreshToken } = await tokenHandler.performTokenRequest(config, tokenRequest) || {};

          console.log('Exchange client token and retrive machine token');

          exchangeClientToken({
            externalAccessToken: accessToken,
            externalRefreshToken: refreshToken,
            orgSubdomain,
            forceCacheUpdate: true,
            clientHostedLogin: true,
          });
        } catch (error) {
          console.log('Exchange client token Error:', error);
        }
      }
    });
  };

  const exchangeClientToken = async ({
    externalAccessToken, externalRefreshToken, orgSubdomain, forceCacheUpdate, clientHostedLogin,
  }: {
    externalAccessToken: string,
    externalRefreshToken?: string,
    orgSubdomain?: string,
    forceCacheUpdate?: boolean,
    clientHostedLogin?: boolean
  }) => {
    console.log({ event: 'EXCHANGE_CLIENT_TOKEN', organizationKey, orgSubdomain });
    const exchangeClientTokenResponse = await exchangeClientTokenMutation({
      variables: {
        input: {
          organizationId: orgSubdomain ?? organizationKey,
          externalClientToken: externalAccessToken,
          externalClientRefreshToken: externalRefreshToken,
          forceCacheUpdate,
        },
      },
    });

    const token = exchangeClientTokenResponse?.data?.exchangeClientToken?.accessToken;
    if (token) {
      setAuthToken(token);
      if (clientHostedLogin) window.localStorage.setItem('clientHostedLoginToken', token);
      await fetchPermissions(token);
      setLoading(false);
    }
  };

  useEffect(() => {
    modifyFavIcon(faviconLink);
    modifyTabTitle(browserTabTitle);
  }, [faviconLink, browserTabTitle]);

  const SignUpNotAllowedDialog = () => (
    <Dialog open={true || openSignUpNotAllowedDialog} fullWidth maxWidth='xs'>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='titleLarge'>{t('signUpNotAllowedDialog.title')}</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant='bodyLarge'>{t('signUpNotAllowedDialog.body')}</Typography>
        <Box mt={5}>
          <Button type='button' fullWidth variant='filled' onClick={appLogout} label={t('shared:okGotIt')} />
        </Box>
      </DialogContent>
    </Dialog>
  );

  const onOrgSelect = (org: Organization) => {
    auth0.loginWithRedirect({ authorizationParams: { redirect_uri: auth0RedirectUri, organization: org.subdomain } }).then(() => {
      window.localStorage.setItem('multipleOrgChosen', 'true');
    });
  };

  const handleHostedLogin = async () => {
    console.log('Initiating Hosted Login process to get Auth code...');
    // 1. Fetch PublicSettings
    const input = {
      ...(organizationKey ? { subdomain: organizationKey } : {}),
      customUrl: window.location.host,
    };

    const resp = await fetchOrganizationPublicSettings({ variables: { input } });
    const clientHostedLoginURL = resp?.data?.fetchOrganizationPublicSettings?.clientHostedLoginURL;
    const clientHostedLoginClientId = resp?.data?.fetchOrganizationPublicSettings?.clientHostedLoginClientId;
    const clientHostedLoginScope = resp?.data?.fetchOrganizationPublicSettings?.clientHostedLoginScope;
    if (!clientHostedLoginURL || !clientHostedLoginClientId || !clientHostedLoginScope) {
      throw new Error('Client Id and hostedLoginURL are required to initiate Hosted Login');
    }

    // 2. Get the authorization code
    await loginPing(clientHostedLoginURL, auth0RedirectUri, clientHostedLoginClientId, clientHostedLoginScope);
  };

  const handleExchangeClientCode = async () => {
    const input = { ...(organizationKey ? { subdomain: organizationKey } : {}), customUrl: window.location.host };

    const resp = await fetchOrganizationPublicSettings({ variables: { input } });
    const clientHostedLoginURL = resp?.data?.fetchOrganizationPublicSettings?.clientHostedLoginURL;
    const orgSubdomain = resp?.data?.fetchOrganizationPublicSettings?.subdomain ?? organizationKey;
    const clientHostedLoginClientId = resp?.data?.fetchOrganizationPublicSettings?.clientHostedLoginClientId;
    const clientHostedLoginScope = resp?.data?.fetchOrganizationPublicSettings?.clientHostedLoginScope;
    if (!clientHostedLoginURL || !clientHostedLoginClientId || !clientHostedLoginScope || !orgSubdomain) {
      throw new Error('Client Id and hostedLoginURL are required to initiate Hosted Login');
    }

    // Exchange code with AccessToken
    await handleCallback(clientHostedLoginURL, auth0RedirectUri, clientHostedLoginClientId, orgSubdomain);
  };

  const isTokenExpired = (token: string) => {
    try {
      const decodedToken: { exp: number } = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch (error) {
      console.error('Client Hosted Login Expired');
      return true;
    }
  };

  const handleClientHostedTokenFromStorage = async () => {
    if (clientHostedLoginToken) {
      if (!isTokenExpired(clientHostedLoginToken)) {
        setAuthToken(clientHostedLoginToken);
        await fetchPermissions(clientHostedLoginToken);
        setLoading(false);
        return;
      }
      window.localStorage.removeItem('clientHostedLoginToken');
      handleExchangeClientCode();
    }
  };

  useEffect(() => {
    console.log({ event: 'AUTH', organizationKey, isOneWealthDomain });

    // check client hosted login token exist in local storage
    if (clientHostedLoginToken) {
      datadogLogs.logger.info('Auth event', { organization: organizationKey, authFlow: 'Client-hosted login token (local storage)' });
      handleClientHostedTokenFromStorage();
      return;
    }

    if (authCode) {
      datadogLogs.logger.info('Auth event', { organization: organizationKey, authFlow: '"Auth Code" exchange process' });
      handleExchangeClientCode();
      return;
    }

    if (isUseHostedLogin) {
      datadogLogs.logger.info('Auth event', { organization: organizationKey, authFlow: 'using Hosted login process' });
      handleHostedLogin();
      return;
    }

    if (externalAccessTokenParams && organizationKey) {
      datadogLogs.logger.info('Auth event', { organization: organizationKey, authFlow: 'Exchanging client token with external access token' });
      exchangeClientToken({ externalAccessToken: externalAccessTokenParams });
      setIsEmbedded(true);
      return;
    }

    if (invitiation) {
      datadogLogs.logger.info('Auth event', { organization: organizationKey, authFlow: 'Invitation' });
      return;
    }

    datadogLogs.logger.info('Auth event', { organization: organizationKey, authFlow: 'Auth0 flow' });

    const handleAccessToMultipleOrgs = (payloadAccessibleOrgs: string[], newToken: string) => {
      if (payloadAccessibleOrgs.length > 1) {
        setAccessibleOrgs([...payloadAccessibleOrgs]);
        setOpenOrganizationSelectDialog(true);
        setAuthToken(newToken);
        setLoading(false);
      } else {
        auth0.loginWithRedirect({
          authorizationParams: {
            redirect_uri: auth0RedirectUri,
            organization: payloadAccessibleOrgs[0],
            ...(connection ? { connection } : {}),
          },
        });
      }
    };

    const getTokenSilentlyAuth0 = async () => {
      auth0.getTokenSilently().then(async (newToken: string) => {
        const payload = parseJwt(newToken);
        const payloadAccessibleOrgs = payload[`${JWT_CLAIMS_NAMESPACE}/accessibleOrgs`];
        const payloadRefreshTokenRequired = payload[`${JWT_CLAIMS_NAMESPACE}/refreshTokenRequired`];
        if (payloadAccessibleOrgs && payloadAccessibleOrgs.length > 0 && !multipleOrgChosen) {
          handleAccessToMultipleOrgs(payloadAccessibleOrgs, newToken);
        } else {
          setAuthToken(newToken);
          const orgUserExists = await fetchPermissions(newToken);
          if (payloadRefreshTokenRequired && orgUserExists) {
            // Refresh token may be needed in case the Auth0 user app_metadata was synched after the current token was issued
            auth0RefreshToken({ orgSubdomain: payload.org_name });
          } else {
            setLoading(false);
          }
        }
      }).catch(async () => {
        let orgSubdomain = organizationKey;
        const input = {
          ...(organizationKey ? { subdomain: organizationKey } : {}),
          customUrl: window.location.host,
        };
        const resp = await fetchOrganizationPublicSettings({ variables: { input } });
        orgSubdomain = resp?.data?.fetchOrganizationPublicSettings?.subdomain;
        const orgConnectionName = resp?.data?.fetchOrganizationPublicSettings?.enterpriseConnectionName || connection;
        const orgUseHostedLogin = resp?.data?.fetchOrganizationPublicSettings?.useHostedLogin;
        if (resp) {
          setOrgPublicSettings(resp?.data?.fetchOrganizationPublicSettings, setFaviconLink, setBrowserTabTitle);
        }
        if (orgUseHostedLogin) {
          handleHostedLogin();
          return;
        }
        const authParams = {
          authorizationParams: {
            redirect_uri: auth0RedirectUri,
            ...(orgSubdomain ? { organization: orgSubdomain } : {}),
            ...(orgConnectionName ? { connection: orgConnectionName } : {}),
          },
        };
        if (allowPopup === 'true') {
          auth0.loginWithPopup(authParams).then((value) => { console.log({ value }); });
        } else {
          auth0.loginWithRedirect(authParams);
        }
      });
    };

    if (state) {
      auth0.handleRedirectCallback().then(() => {
        auth0.getTokenSilently().then(async (newToken: string) => {
          const payload = parseJwt(newToken);
          const respForLoggedUser = await fetchOrganizationPublicSettings({ variables: { input: { subdomain: payload.org_name } } });

          if (respForLoggedUser) {
            setOrgPublicSettings(respForLoggedUser?.data?.fetchOrganizationPublicSettings, setFaviconLink, setBrowserTabTitle);
          }

          const payloadAccessibleOrgs = payload[`${JWT_CLAIMS_NAMESPACE}/accessibleOrgs`];
          const payloadRefreshTokenRequired = payload[`${JWT_CLAIMS_NAMESPACE}/refreshTokenRequired`];
          if (payloadAccessibleOrgs && payloadAccessibleOrgs.length > 0 && !multipleOrgChosen) {
            handleAccessToMultipleOrgs(payloadAccessibleOrgs, newToken);
          } else {
            console.log({ event: 'AUTHENTICATED_USER', userReferenceId: payload.sub });
            setAuthToken(newToken);
            const orgUserExists = await fetchPermissions(newToken);
            if (payloadRefreshTokenRequired && orgUserExists) {
              // Refresh token may be needed in case the Auth0 user app_metadata was synched after the current token was issued
              auth0RefreshToken({ orgSubdomain: payload.org_name });
            } else {
              setLoading(false);
              const path = window.localStorage.getItem('auth0RedirectPath');
              if (path) {
                const auth0RedirectPath = JSON.parse(path);
                if (dayjs().isBefore(auth0RedirectPath.expireAt)) {
                  window.history.replaceState(null, '', auth0RedirectPath.locationPath);
                }
                window.localStorage.removeItem('auth0RedirectPath');
              }
            }
          }
        }).catch(async (error) => {
          console.log({ error });
          let orgSubdomain = organizationKey;
          const input = { ...(organizationKey ? { subdomain: organizationKey } : {}), customUrl: window.location.host };
          const resp = await fetchOrganizationPublicSettings({ variables: { input } });

          orgSubdomain = resp?.data?.fetchOrganizationPublicSettings?.subdomain;
          const orgConnectionName = resp?.data?.fetchOrganizationPublicSettings?.enterpriseConnectionName || connection;
          if (resp) {
            setOrgPublicSettings(resp?.data?.fetchOrganizationPublicSettings, setFaviconLink, setBrowserTabTitle);
          }
          auth0.loginWithRedirect({
            authorizationParams: {
              redirect_uri: auth0RedirectUri,
              ...(orgSubdomain ? { organization: orgSubdomain } : {}),
              ...(orgConnectionName ? { connection: orgConnectionName } : {}),
            },
          });
        });
      }).catch(() => {
        getTokenSilentlyAuth0();
      });
    } else {
      getTokenSilentlyAuth0();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <Box display='flex' alignItems='center' justifyContent='center' height='100vh' margin='auto'>
          <CircularProgress />
        </Box>
      ) : accessibleOrgs && accessibleOrgs.length > 0 && !multipleOrgChosen ? (
        <PublicOrganizationSelectDialog open={openOrganizationSelectDialog} onOrgSelect={onOrgSelect} filter={{ referenceIds: accessibleOrgs }} />
      ) : openSignUpNotAllowedDialog ? (
        <SignUpNotAllowedDialog />
      ) : (
        children
      )}
    </>
  );
};

export default Auth0Provider;
